/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXResetter,
} from '@cloudio-saas/statex';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Congrads from 'api/Congrads';
import { developerModeAtom } from 'designer/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React, { startTransition } from 'react';
import {
  activeSidebarPanelAtom,
  PageView,
  sidebarActivePageIdAtom,
} from '../../../atoms';
import { WizardOption } from '../../common';
import { useUserPagesStore } from 'store/utils/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    gap: '16px',
    backgroundColor: theme.palette.background.default,
  },
}));

const PageWizardNext = ({
  setView,
  appUid,
}: {
  appUid: string;
  setView: (_view: PageView) => void;
}): React.ReactElement | null => {
  const id = useStateXAtomValue(sidebarActivePageIdAtom);
  const classes = useStyles();
  const platform = usePlatform();
  const reset = useStateXResetter();
  const setDevMode = useStateXAtomSetter(developerModeAtom, {
    params: { appUid },
  });
  const pageStore = useUserPagesStore();
  pageStore.refresh();
  return (
    <div className={classes.root}>
      <Congrads msg={`Page '${id}' successfully created!`} />
      <WizardOption
        data-testid="edit"
        iconName="edit"
        title="Edit Page"
        description="Navigate to Page Edit view"
        onClick={async () => {
          setView('edit');
        }}
      />
      <WizardOption
        data-testid="access"
        iconName="user-shield"
        title="Access Settings"
        description="Navigate to Access Settings view to grant access to this Page for various roles"
        onClick={async () => {
          startTransition(() => {
            setView('access');
          });
        }}
      />
      <WizardOption
        data-testid="nav"
        iconName="desktop"
        title="Navigate To Page"
        description="Navigate to the newly created page."
        onClick={async () => {
          startTransition(() => {
            setDevMode(false);
            reset(activeSidebarPanelAtom);
            platform.goToPage(appUid, id);
            setView('list');
          });
        }}
      />
      <WizardOption
        data-testid="open-designer"
        iconName="desktop"
        title="Open Page in Designer"
        description="Navigate to the newly created page."
        onClick={async () => {
          startTransition(() => {
            platform.goToPage(appUid, id);
            reset(activeSidebarPanelAtom);
            setDevMode(true);
            setView('list');
          });
        }}
      />
    </div>
  );
};

export default PageWizardNext;
